// src/pages/league-dashboard/TeamStats.js
// Team Statistics Display with DynastyTable 3-Line Format

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertCircle, RefreshCw,
  DollarSign, Users, Filter, ExternalLink
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { useCommissioner } from '../../contexts/CommissionerContext';
import { usePlayerModal } from '../../contexts/PlayerModalContext';
import CommissionerModeBar from '../../components/commissioner/CommissionerModeBar';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import { 
  DynastyTable
} from '../../services/tableService';
import {
  createTeamStatsActiveLineupColumns,
  createTeamStatsReserveColumns,
  separateCurrentAndHistorical,
  createTeamStatsActiveLineupColumnsAccruedOnly,
  createTeamStatsReserveColumnsAccruedOnly
} from '../../services/tables/teamStatsColumns';
import { computeTotals } from '../../services/tables/statTotals';
import {
  getStatConfigs,
  DEFAULT_BATTING_STATS,
  DEFAULT_PITCHING_STATS
} from '../../utils/statMapping';
import { classifyPlayerSection } from '../../utils/playerClassification';

const TeamStats = ({ leagueId, league, user, initialViewTeamId, initialViewTeamName, userTeam, bannerConfig }) => {
  const navigate = useNavigate();
  const { openPlayerModal } = usePlayerModal();
  const {
    isCommissionerMode,
    activeTeamName,
    activeTeamId,
    isCommissioner
  } = useCommissioner();

  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('hitting');
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [accruedOnlyMode, setAccruedOnlyMode] = useState(true);
  
  // Team selection state
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [userTeamId, setUserTeamId] = useState(null);
  const [actualTeamData, setActualTeamData] = useState(null);
  const [viewedTeamInfo, setViewedTeamInfo] = useState(null);


  // ========================================
  // DYNAMIC LEAGUE POSITIONS
  // ========================================
  const getLeaguePositions = () => {
    const positions = {
      hitting: [],
      pitching: []
    };

    if (league?.roster_settings?.position_requirements && typeof league.roster_settings.position_requirements === 'object') {
      const posReqs = league.roster_settings.position_requirements;
      
      Object.entries(posReqs).forEach(([positionKey, config]) => {
        const slots = config?.slots || 0;
        
        if (positionKey === 'P' && slots > 0) {
          positions.pitching.push(...Array(slots).fill('P'));
        } else if (slots > 0) {
          positions.hitting.push(...Array(slots).fill(positionKey));
        }
      });
    } else {
      // Fallback defaults
      positions.hitting = ['C', 'C', '1B', '2B', 'SS', '3B', 'MI', 'CI', 'OF', 'OF', 'OF', 'OF'];
      positions.pitching = Array(10).fill('P');
    }
    
    return positions;
  };

  const positions = getLeaguePositions();

  // ========================================
  // DYNAMIC STAT CATEGORIES FROM LEAGUE
  // ========================================
  const statCategories = useMemo(() => {
    if (league?.league_settings?.scoring_categories) {
      const leagueHitting = league.league_settings.scoring_categories.hitting || DEFAULT_BATTING_STATS;
      const leaguePitching = league.league_settings.scoring_categories.pitching || DEFAULT_PITCHING_STATS;

      // Prepend fundamental stats that aren't scoring categories but are essential context
      const hittingPrepend = ['G', 'AB', 'H'].filter(s => !leagueHitting.includes(s));
      const pitchingPrepend = ['G', 'IP', 'R_P', 'ER'].filter(s => !leaguePitching.includes(s));

      return {
        hitting: [...hittingPrepend, ...leagueHitting],
        pitching: [...pitchingPrepend, ...leaguePitching]
      };
    }
    
    return {
      hitting: DEFAULT_BATTING_STATS,
      pitching: DEFAULT_PITCHING_STATS
    };
  }, [league]);

  // Get stat configs for current tab
  const currentStatConfigs = useMemo(() => {
    const statLabels = activeTab === 'hitting' ? statCategories.hitting : statCategories.pitching;
    const isPitcher = activeTab === 'pitching';
    return getStatConfigs(statLabels, isPitcher);
  }, [activeTab, statCategories]);

  // ========================================
  // DATA LOADING
  // ========================================
  const loadAllTeams = async () => {
    try {
      const response = await leaguesAPI.getLeagueTeams(leagueId);

      if (response.success && response.teams) {
        const teamList = response.teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name || 'Unnamed Team',
          manager_name: team.manager_name || 'Unknown Manager',
          is_user_team: team.is_user_team || false,
          team_logo_url: team.team_logo_url || null,
          team_colors: team.team_colors || null,
          team_motto: team.team_motto || null
        }));

        setAllTeams(teamList);

        const foundUserTeam = teamList.find(t => t.is_user_team);
        if (foundUserTeam) {
          setUserTeamId(foundUserTeam.team_id);
          // 🔧 FIX: Don't auto-select user team if we're navigating to view another team
          if (!selectedTeamId && !isCommissionerMode && !initialViewTeamId) {
            setSelectedTeamId(foundUserTeam.team_id);
            setViewedTeamInfo(foundUserTeam);
          }
        }
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  const loadTeamStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      let teamId;
      
      // Determine which team to load
      if (isCommissionerMode && activeTeamId) {
        teamId = activeTeamId;
      } else if (selectedTeamId) {
        teamId = selectedTeamId;
      } else if (userTeamId) {
        teamId = userTeamId;
      }
      
      // USE CANONICAL ENDPOINTS LIKE MYROSTER
      if (!teamId || teamId === userTeamId) {
        // Use canonical API for own roster
        response = await leaguesAPI.getMyRosterCanonical(leagueId);
      } else {
        // Use canonical API for viewing other teams
        response = await leaguesAPI.getTeamRosterCanonical(leagueId, teamId);
      }
      
      if (response && response.success) {
        // Canonical response format: { success, team_id, team_name, players: [...] }
        console.log('Loaded canonical roster data:', response.players?.length, 'players');
        setStats(response.players || []);
        setRecentTransactions([]);  // TODO: Get transactions separately
        
        if (response.team_name) {
          setActualTeamData({
            team_name: response.team_name,
            team_id: response.team_id
          });
        }
      } else {
        setError(response?.message || 'Failed to load team statistics');
      }
    } catch (err) {
      console.error('Error loading team stats:', err);
      setError('Failed to load team statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FILTER AND ORGANIZE DATA
  // ========================================
  const { currentRoster, historicalPlayers } = useMemo(() => {
    const { current, historical } = separateCurrentAndHistorical(stats);
    return { currentRoster: current, historicalPlayers: historical };
  }, [stats]);

  // ========================================
  // ORGANIZE ROSTER DATA INTO POSITION SLOTS
  // ========================================
  const organizeRosterForTable = useMemo(() => {
    const organized = {
      hitting: { active: [], nonActive: [] },
      pitching: { active: [], nonActive: [] }
    };

    // Create position slot rows for active lineup
    const batterPositionCounts = {};
    positions.hitting.forEach((pos) => {
      const count = batterPositionCounts[pos] || 0;
      const slotId = `${pos}_${count}`;
      organized.hitting.active.push({
        slotPosition: pos,
        slotId: slotId,
        player: null
      });
      batterPositionCounts[pos] = count + 1;
    });

    const pitcherPositionCounts = {};
    positions.pitching.forEach((pos) => {
      const count = pitcherPositionCounts[pos] || 0;
      const slotId = `${pos}_${count}`;
      organized.pitching.active.push({
        slotPosition: pos,
        slotId: slotId,
        player: null
      });
      pitcherPositionCounts[pos] = count + 1;
    });

    // Parse canonical players and assign to slots
    currentRoster.forEach(canonicalPlayer => {
      const player = canonicalPlayer;
      // Slot-based section classification (Bible §4.5.9.1): a TWP active at a
      // P slot belongs to 'pitching' and matches that slot.
      const category = classifyPlayerSection(player);

      const status = player.league_context?.roster?.status;
      const assignedPosition = player.league_context?.roster?.position;

      // Only assign to active lineup if player has 'active' status AND a position
      // DL/bench/minors players go to nonActive (even if they have accrued stats)
      if (status === 'active' && assignedPosition) {
        const slot = organized[category].active.find(s => s.slotId === assignedPosition);
        if (slot) {
          slot.player = player;
        } else {
          // If slot not found, add to first empty slot
          const emptySlot = organized[category].active.find(s => !s.player);
          if (emptySlot) {
            emptySlot.player = player;
          } else {
            organized[category].nonActive.push(player);
          }
        }
      } else if (status === 'active' && !assignedPosition) {
        // Active player without position - try to put in first empty slot
        const emptySlot = organized[category].active.find(s => !s.player);
        if (emptySlot) {
          emptySlot.player = player;
        } else {
          organized[category].nonActive.push(player);
        }
      } else {
        // Non-active players (bench, DL, minors) go to nonActive
        organized[category].nonActive.push(player);
      }
    });

    return organized;
  }, [currentRoster, positions]);

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getCurrentTeamName = () => {
    // 🚨 CRITICAL: Don't trust backend's team_name - look up from allTeams
    let targetTeamId = null;
    
    if (isCommissionerMode && activeTeamId) {
      targetTeamId = activeTeamId;
    } else if (selectedTeamId) {
      targetTeamId = selectedTeamId;
    } else if (actualTeamData?.team_id) {
      targetTeamId = actualTeamData.team_id;
    } else if (userTeamId) {
      targetTeamId = userTeamId;
    }
    
    // Look up the actual team name from allTeams
    if (targetTeamId && allTeams.length > 0) {
      const team = allTeams.find(t => t.team_id === targetTeamId);
      if (team?.team_name) {
        return team.team_name;
      }
    }
    
    return 'Your Team';
  };

  const handleTeamChange = (e) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);
    // Update viewed team info for the banner
    const team = allTeams.find(t => t.team_id === newTeamId);
    if (team) {
      setViewedTeamInfo(team);
    }
  };

  const handlePlayerClick = (player) => {
    const playerId = player.ids?.mlb;
    openPlayerModal(playerId, player);
  };

  const handleMovePlayer = (player) => {
    console.log('Move player:', player.info?.full_name);
    // TODO: Implement move functionality
  };

  const handleDropPlayer = (player) => {
    console.log('Drop player:', player.info?.full_name);
    // TODO: Implement drop functionality
  };

  const handleTradePlayer = (player) => {
    console.log('Trade player:', player.info?.full_name);
    // TODO: Implement trade functionality
  };

  // Check if league has bench slots
  const hasBenchSlots = league?.roster_settings?.position_requirements?.BN?.slots > 0 || false;
  
  const leagueSettings = {
    enableMinors: league?.roster_settings?.position_requirements?.MIN?.slots > 0 || false,
    enableDL: league?.roster_settings?.position_requirements?.DL?.slots > 0 || false
  };

  // Pass raw canonical players - NO TRANSFORMS
  const activeLineupRows = useMemo(() => {
    return currentRoster.filter(player => {
      const category = classifyPlayerSection(player);
      const status = player.league_context?.roster?.status;

      if (activeTab === 'hitting') return category === 'hitting' && status === 'active';
      if (activeTab === 'pitching') return category === 'pitching' && status === 'active';
      return false;
    });
  }, [currentRoster, activeTab]);

  // Filter non-active players - NO TRANSFORMS, return raw canonical players
  const nonActiveRows = useMemo(() => {
    const category = activeTab;
    const statCategory = activeTab === 'pitching' ? 'pitching' : 'hitting';

    // Filter for players WITH stats in accrued mode
    let filtered = organizeRosterForTable[category].nonActive;

    if (accruedOnlyMode) {
      filtered = filtered.filter(player => {
        const stats = player.stats?.active_accrued?.[statCategory];
        if (!stats) return false;
        // Check if player has any non-zero stat values
        const hasStats = Object.entries(stats).some(([key, val]) => val && val !== 0);
        return hasStats;
      });
    }

    // Return raw canonical players - DynastyTable handles display logic
    return filtered;

  }, [organizeRosterForTable, activeTab, accruedOnlyMode]);
  const historicalRows = useMemo(() => {
    // Filter historical players - return raw canonical players
    const filtered = historicalPlayers.filter(p => {
      // Historical/former players have no active slot → classifyPlayerSection
      // falls back to primary position (TWP → hitting), preserving behavior.
      const category = classifyPlayerSection(p);
      if (activeTab === 'hitting') return category === 'hitting';
      if (activeTab === 'pitching') return category === 'pitching';
      return false;
    });
    // Return raw canonical players - DynastyTable handles display logic
    return filtered;
  }, [historicalPlayers, activeTab]);

  // Calculate totals and add totals row when in accrued-only mode
  const activeLineupRowsWithTotals = useMemo(() => {
    if (!accruedOnlyMode || activeLineupRows.length === 0) return activeLineupRows;
    
    const totals = computeTotals(activeLineupRows, { statType: activeTab === 'pitching' ? 'pitching' : 'hitting', window: 'active_accrued' }); // DD-STAT-CONSOLIDATION P0: SSoT engine (component recompute, no fallbacks)
    
    return [
      ...activeLineupRows,
      {
        id: 'totals-active-lineup',
        isTotalsRow: true,
        totals: totals
      }
    ];
  }, [activeLineupRows, accruedOnlyMode, activeTab]);

  const nonActiveRowsWithTotals = useMemo(() => {
    if (!accruedOnlyMode || nonActiveRows.length === 0) return nonActiveRows;

    // Don't show totals for 1 player — it's just that player's stats repeated
    if (nonActiveRows.length <= 1) return nonActiveRows;

    const totals = computeTotals(nonActiveRows, { statType: activeTab === 'pitching' ? 'pitching' : 'hitting', window: 'active_accrued' }); // DD-STAT-CONSOLIDATION P0: SSoT engine (component recompute, no fallbacks)

    return [
      ...nonActiveRows,
      {
        id: 'totals-non-active',
        isTotalsRow: true,
        totals: totals
      }
    ];
  }, [nonActiveRows, accruedOnlyMode, activeTab]);

  // Combine Active Lineup + Other Players totals into Team Totals
  // Calculate directly from raw row arrays — NOT from sub-table totals rows,
  // because nonActiveRowsWithTotals skips the totals row when there's only 1 player.
  const teamTotalsRows = useMemo(() => {
    if (!accruedOnlyMode) return [];

    const allRows = [...activeLineupRows, ...nonActiveRows];
    if (allRows.length === 0) return [];

    const combinedTotals = computeTotals(allRows, { statType: activeTab === 'pitching' ? 'pitching' : 'hitting', window: 'active_accrued' }); // DD-STAT-CONSOLIDATION P0: SSoT engine (component recompute, no fallbacks)

    // TRANSFORM: Map flat keys to nested column keys
    const transformedTotals = { ...combinedTotals };
    currentStatConfigs.forEach(config => {
      const simpleField = config.field.split('.').pop();
      const nestedKey = `stats.${config.field}`;
      if (combinedTotals[simpleField] !== undefined) {
        transformedTotals[nestedKey] = combinedTotals[simpleField];
      }
    });

    return [{
      id: 'team-totals',
      ...transformedTotals
    }];
  }, [accruedOnlyMode, activeLineupRows, nonActiveRows, currentStatConfigs, activeTab]);

  // DD-STAT-CONSOLIDATION P0: removed dead `historicalRowsWithTotals` memo — the
  // Historical/Alumni table renders `historicalRows` (no totals row); a team-totals
  // row over former players' active_accrued stats is meaningless and was never rendered.

  // Create columns (conditional based on accrued-only mode)
  const activeLineupColumns = useMemo(() => {
    if (accruedOnlyMode) {
      return createTeamStatsActiveLineupColumnsAccruedOnly({
        statConfigs: currentStatConfigs,
        onPlayerClick: handlePlayerClick,
        showHistoricalBadge: false,
        isPitcher: activeTab === 'pitching',
        onMovePlayer: handleMovePlayer,
        onDropPlayer: handleDropPlayer,
        onTradePlayer: handleTradePlayer,
        hasBenchSlots: hasBenchSlots,
        leagueSettings: leagueSettings
      });
    }
    return createTeamStatsActiveLineupColumns({
      statConfigs: currentStatConfigs,
      onPlayerClick: handlePlayerClick,
      showHistoricalBadge: false,
      isPitcher: activeTab === 'pitching',
      onMovePlayer: handleMovePlayer,
      onDropPlayer: handleDropPlayer,
      onTradePlayer: handleTradePlayer,
      hasBenchSlots: hasBenchSlots,
      leagueSettings: leagueSettings
    });
  }, [currentStatConfigs, activeTab, hasBenchSlots, leagueSettings, accruedOnlyMode]);

  const nonActiveColumns = useMemo(() => {
    if (accruedOnlyMode) {
      return createTeamStatsReserveColumnsAccruedOnly({
        statConfigs: currentStatConfigs,
        onPlayerClick: handlePlayerClick,
        showHistoricalBadge: false,
        isPitcher: activeTab === 'pitching'
      });
    }
    return createTeamStatsReserveColumns({
      statConfigs: currentStatConfigs,
      onPlayerClick: handlePlayerClick,
      showHistoricalBadge: false,
      isPitcher: activeTab === 'pitching'
    });
  }, [currentStatConfigs, activeTab, accruedOnlyMode]);

  const teamTotalsColumns = useMemo(() => {
    if (!accruedOnlyMode) return [];
    return createTeamStatsReserveColumnsAccruedOnly({
      statConfigs: currentStatConfigs,
      onPlayerClick: handlePlayerClick,
      showHistoricalBadge: false,
      isPitcher: activeTab === 'pitching',
      isTeamTotalsTable: true
    });
  }, [currentStatConfigs, activeTab, accruedOnlyMode]);

  const historicalColumns = useMemo(() => {
    if (accruedOnlyMode) {
      return createTeamStatsReserveColumnsAccruedOnly({
        statConfigs: currentStatConfigs,
        onPlayerClick: handlePlayerClick,
        showHistoricalBadge: true,
        isPitcher: activeTab === 'pitching'
      });
    }
    return createTeamStatsReserveColumns({
      statConfigs: currentStatConfigs,
      onPlayerClick: handlePlayerClick,
      showHistoricalBadge: true,
      isPitcher: activeTab === 'pitching'
    });
  }, [currentStatConfigs, activeTab, accruedOnlyMode]);

  // ========================================
  // EFFECTS
  // ========================================
  useEffect(() => {
    if (leagueId) loadAllTeams();
  }, [leagueId]);
  
  useEffect(() => {
    if (initialViewTeamId) {
      setSelectedTeamId(initialViewTeamId);
    }
  }, [initialViewTeamId]);

  useEffect(() => {
    if (leagueId && (selectedTeamId || userTeamId || (isCommissionerMode && activeTeamId))) {
      loadTeamStats();
    }
  }, [leagueId, selectedTeamId, userTeamId, isCommissionerMode, activeTeamId]);

  // Update viewedTeamInfo when team selection changes
  useEffect(() => {
    if (allTeams.length > 0) {
      const targetTeamId = isCommissionerMode && activeTeamId
        ? activeTeamId
        : selectedTeamId || userTeamId;
      const team = allTeams.find(t => t.team_id === targetTeamId);
      if (team) {
        setViewedTeamInfo(team);
      }
    }
  }, [selectedTeamId, userTeamId, activeTeamId, isCommissionerMode, allTeams]);

  // Get the current team's display info (pick ONE team object, then read fields)
  const activeTeamData = viewedTeamInfo || userTeam;
  const displayTeamName = viewedTeamInfo?.team_name || getCurrentTeamName();
  const logoUrl = activeTeamData?.team_logo_url;
  const teamColors = activeTeamData?.team_colors;
  const teamMotto = activeTeamData?.team_motto;

  // ========================================
  // MAIN RENDER
  // ========================================
  if (loading) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
        <RefreshCw className={`w-8 h-8 animate-spin ${dynastyTheme.classes.text.primary} mx-auto mb-4`} />
        <p className={dynastyTheme.classes.text.white}>Loading team statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={loadTeamStats}
          className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Commissioner Mode Bar */}
      <CommissionerModeBar 
        league={league}
        onTeamSwitch={() => loadTeamStats()}
      />

      <TeamPageHeader
        title={`${displayTeamName} Stats`}
        logoUrl={logoUrl}
        teamName={displayTeamName}
        teamColors={teamColors}
        teamMotto={teamMotto}
        bannerConfig={bannerConfig}
        isCommissioner={isCommissioner}
        subtitle={
          <div className="flex items-center gap-2">
            <Users className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`} />
            <span className={dynastyTheme.classes.text.neutralLight}>
              Active: <span className={dynastyTheme.classes.text.white}>{activeLineupRows.length} players</span>
            </span>
          </div>
        }
        actions={
          <>
            <button
              onClick={() => setAccruedOnlyMode(!accruedOnlyMode)}
              className={dynastyTheme.utils.getComponent('button', accruedOnlyMode ? 'primary' : 'secondary', 'md')}
            >
              <Activity className="w-4 h-4 mr-2" />
              {accruedOnlyMode ? 'All Stats' : 'Summary View'}
            </button>
            <button
              onClick={loadTeamStats}
              disabled={loading}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      />

      {/* DD-STAT-CONSOLIDATION P0: removed the permanently-null "Team Totals" summary band
          (teamTotals was hard-set to null → never rendered; its cells were all `|| 0`
          fallbacks). The real per-table Team Totals come from the statTotals engine below. */}

      {/* Position Tabs + Team Dropdown */}
      <div className={`flex items-center justify-between border-b ${dynastyTheme.classes.border.neutral}`} style={{ marginTop: 0 }}>
        {/* Left: Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('hitting')}
            className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'hitting'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
            }`}
          >
            Batters ({positions.hitting.length} slots)
          </button>
          <button
            onClick={() => setActiveTab('pitching')}
            className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'pitching'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
            }`}
          >
            Pitchers ({positions.pitching.length} slots)
          </button>
        </div>

        {/* Right: Team Selection Dropdown */}
        {!isCommissionerMode && allTeams.length > 0 && (
          <select
            value={selectedTeamId || userTeamId || ''}
            onChange={handleTeamChange}
            className={`${dynastyTheme.components.input} px-3 py-1.5 min-w-[200px] text-sm`}
          >
            {allTeams.map(team => {
              const suffix = team.is_user_team ? ' (Your Team)' : '';
              return (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name}{suffix}
                </option>
              );
            })}
          </select>
        )}
      </div>

      {/* ACTIVE LINEUP TABLE */}
      <div>
        <div className="flex items-center gap-2" style={{ padding: '9px 0' }}>
          <span style={{ width: 3, height: 15, borderRadius: 2, background: dynastyTheme.tokens.colors.primary }} />
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.primary, lineHeight: 1 }}>
            Active Lineup
          </span>
        </div>

        {activeLineupRowsWithTotals.length > 0 ? (
          <DynastyTable
            data={activeLineupRowsWithTotals}
            columns={activeLineupColumns}
            tableType="roster-lineup"
            leaguePositions={positions}
            playerType={activeTab}
            rowsPerItem={accruedOnlyMode ? 1 : 3}
            rowTypes={accruedOnlyMode ? ['accrued'] : ['season', 'rolling', 'accrued']}
            stickyHeader={true}
            enableHorizontalScroll={true}
            enableVerticalScroll={false}
            maxHeight="none"
            minWidth="800px"
          />
        ) : (
          <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
            <Users className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
            <p className={dynastyTheme.classes.text.neutralLight}>
              No active lineup configured
            </p>
          </div>
        )}
      </div>

      {/* NON-ACTIVE PLAYERS TABLE (bench, DL, minors, etc) */}
      {nonActiveRowsWithTotals.length > 0 && (
        <div>
          <div className="flex items-center gap-2" style={{ padding: '9px 0' }}>
            <span style={{ width: 3, height: 15, borderRadius: 2, background: dynastyTheme.tokens.colors.primary }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.primary, lineHeight: 1 }}>
              Other Players with Accrued Stats
            </span>
            <span style={{ fontSize: 11, color: dynastyTheme.tokens.colors.neutral[500] }}>
              ({organizeRosterForTable[activeTab].nonActive.length})
            </span>
          </div>

          <DynastyTable
            data={nonActiveRowsWithTotals}
            columns={nonActiveColumns}
            tableType="player-list"
            stickyHeader={true}
            enableHorizontalScroll={true}
            enableVerticalScroll={false}
            maxHeight="none"
            minWidth="700px"
          />
        </div>
      )}

      {/* TEAM TOTALS TABLE */}
      {teamTotalsRows.length > 0 && accruedOnlyMode && (
        <div>
          <div className="flex items-center gap-2" style={{ padding: '9px 0' }}>
            <span style={{ width: 3, height: 15, borderRadius: 2, background: dynastyTheme.tokens.colors.primary }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.primary, lineHeight: 1 }}>
              Team Totals
            </span>
          </div>

          <DynastyTable
            data={teamTotalsRows}
            columns={teamTotalsColumns}
            tableType="flat-data"
            stickyHeader={true}
            enableHorizontalScroll={true}
            enableVerticalScroll={false}
            maxHeight="none"
            minWidth="700px"
          />
        </div>
      )}

      {/* HISTORICAL PLAYERS TABLE */}
      {historicalRows.length > 0 && (
        <div>
          <div className="flex items-center gap-2" style={{ padding: '9px 0' }}>
            <span style={{ width: 3, height: 15, borderRadius: 2, background: dynastyTheme.tokens.colors.primary }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.primary, lineHeight: 1 }}>
              Historical Stats - Alumni
            </span>
            <span style={{ fontSize: 11, color: dynastyTheme.tokens.colors.neutral[500] }}>
              ({Math.floor(historicalRows.length / 3)} players)
            </span>
          </div>
          
          <DynastyTable
            data={historicalRows}
            columns={historicalColumns}
            tableType="player-list"
            stickyHeader={true}
            enableHorizontalScroll={true}
            enableVerticalScroll={false}
            maxHeight="none"
            minWidth="700px"
          />
        </div>
      )}

      {/* Recent Transactions */}
      {recentTransactions && recentTransactions.length > 0 && (
        <div className={dynastyTheme.components.card.base}>
          <div className="p-6">
            <div className="flex items-center gap-2" style={{ padding: '9px 0' }}>
              <span style={{ width: 3, height: 15, borderRadius: 2, background: dynastyTheme.tokens.colors.primary }} />
              <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.primary, lineHeight: 1 }}>
                Recent Transactions
              </span>
            </div>
            <div className="space-y-2">
              {recentTransactions.map((transaction, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between py-2 px-3 rounded ${dynastyTheme.classes.bg.darkLighter}`}
                >
                  <div className={dynastyTheme.classes.text.white}>
                    {transaction.type === 'add' && '➕'}
                    {transaction.type === 'drop' && '➖'}
                    {transaction.type === 'trade' && '🔄'}
                    <span className="ml-2">{transaction.player}</span>
                    {transaction.details && (
                      <span className={`ml-2 ${dynastyTheme.classes.text.neutralLight} text-sm`}>
                        - {transaction.details}
                      </span>
                    )}
                  </div>
                  <div className={dynastyTheme.classes.text.neutralLight}>
                    {new Date(transaction.date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamStats;
